import * as Parse from './parse-preference';

/**
 * @name default
 * @inner
 * @type {Object}
 * @param {WidgetAdapter} widget A widget instance adapter
 * @param {string} contextRoot The portal context root
 * @param {Promise} Promise An ES2015 compliant promise
 * @returns {Widget} The widget service
 */
export default (widget, contextRoot, Promise) => ({
  /**
   * A service that provides access to the instance of the widget in the portal.
   *
   * @name Widget
   * @type {object}
   */

  /**
   * Gets a preference from widget configuration (model.xml) and attempts to return it as a string
   *
   * *DEPRECATED*
   * `getPreference` is deprecated in favor of the type specific `get<Type>Preference` methods.
   * This makes the use of the preferences in the context of a widget more reliable, as they are
   * parsed/coerced into the expected type in a reliable and consistent way, instead of ad-hoc
   * whenever a preference is used.
   *
   * @name Widget#getPreference
   * @deprecated 0.2.3
   * @type {function}
   * @param {string} name
   * @returns {?string} The parsed value of the preference
   */
  getPreference: (name) => {
    console.warn( // eslint-disable-line no-console
      '`getPreference` is deprecated - use the type specific `get<Type>Preference` method instead.'
    );
    return widget.getPreference(name);
  },

  /**
   * Gets a preference from widget configuration (model.xml) and attempts to return it as a string
   *
   * @name Widget#getStringPreference
   * @type {function}
   * @param {string} name
   * @returns {?string} The parsed value of the preference
   */
  getStringPreference: (name) => {
    const str = Parse.toString(widget.getPreference(name));
    return (str === undefined) ? str : str
      .replace('$(contextRoot)', contextRoot);
  },

  /**
   * Gets a preference from widget configuration (model.xml) and attempts to return it as a integer
   * number.
   *
   * @name Widget#getLongPreference
   * @type {function}
   * @param {string} name
   * @returns {?number} The parsed value of the preference
   */
  getLongPreference: (name) =>
    Parse.toLong(widget.getPreference(name)),

  /**
   * Gets a preference from widget configuration (model.xml) and attempts to return it as a decimal
   * number.
   *
   * @name Widget#getDoublePreference
   * @type {function}
   * @param {string} name
   * @returns {?number} The parsed value of the preference
   */
  getDoublePreference: (name) =>
    Parse.toDouble(widget.getPreference(name)),

  /**
   * Gets a preference from widget configuration (model.xml) and attempts to return it as a boolean.
   *
   * @name Widget#getBooleanPreference
   * @type {function}
   * @param {string} name
   * @returns {?boolean} The parsed value of the preference
   */
  getBooleanPreference: (name) =>
    Parse.toBoolean(widget.getPreference(name)),

  /**
   * Gets a preference from widget configuration (model.xml) and attempts to return it as an array
   * of strings, split on commas.
   *
   * @name Widget#getStringArrayPreference
   * @type {function}
   * @param {string} name
   * @returns {?Array.<string>} The parsed value of the preference
   */
  getStringArrayPreference: (name) =>
    Parse.toStringArray(widget.getPreference(name)),

  /**
   * Gets a preference from widget configuration (model.xml) and attempts to return it as an null.
   *
   * @name Widget#getNullPreference
   * @type {function}
   * @param {string} name
   * @returns {?null} The parsed value of the preference
   */
  getNullPreference: (name) =>
    Parse.toNull(widget.getPreference(name)),

  /**
   * Gets a preference from widget as returned by portal client. This method is provided
   * as a "escape hatch" when none of the types methods work, but should generally be
   * avoided in common use due to its reliance on the underlying portal client implementation.
   *
   * *N.B.* The return type is dependant on the underlying portal client implementation, and may
   * change across portal client versions.
   *
   * @name Widget#getRawPreference
   * @type {function}
   * @param {string} name
   * @returns {any} The value of the preference directly from the portal client
   */
  getRawPreference: (name) =>
    widget.getPreference(name),

  /**
   * Sets a given value for a given preference
   *
   * *DEPRECATED*
   * `setPreference` is deprecated in favor of `savePreference` which also persists the value to
   * the portal.
   *
   * @name Widget#setPreference
   * @deprecated 1.1.0
   * @type {function}
   * @param {string} name
   * @param {string} value
   * @returns {object|string} preference
   */
  setPreference: (name, value) => {
    console.warn( // eslint-disable-line no-console
      '`setPreference` is deprecated - use `savePreference` to set and persist the preference'
    );
    return widget.setPreference(name, value);
  },

  /**
   * Sets a given value for a given preference and persists it to the portal.
   *
   * @name Widget#savePreference
   * @since 1.1.0
   * @type {function}
   * @param {string} name
   * @param {string} value
   * @returns {Promise.<void>}
   */
  savePreference: (name, value) => new Promise(resolve => {
    widget.setPreference(name, value);
    widget.saveModel(() => { resolve(); });
  }),

  /**
   * @name Widget#getId
   * @type {function}
   * @description Returns the widget's instance ID
   * @returns {string}
   */
  getId: () => widget.id,
});



// WEBPACK FOOTER //
// ./lib-bb-widget/scripts/widget.js